import { BinaryQuestionTemplate } from '@templates';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../routes/Routes';
import { ZodError } from 'zod';
import { ErrorMessages } from '@types';
import { contactUserOptionsSchema } from '../../validations/index';
import { handleLoginPageEvent, msalInstance, parseZodErrors } from '@utils';
import {
  useBuskingStore,
  useBuskingPayload,
  useHandleSubmit,
} from '../../../../hooks';
import { useUserData } from '@hooks';
import { InformationBox } from '@molecules/*';
export const InfoText = () => (
  <div className="flex flex-col gap-2">
    <p>
      Please note if you select no, we may not be able to fully investigate your
      issue.
    </p>
  </div>
);

const infoBoxTitle = 'Important';
const pageTitle = `Can we contact you about your issue?`;
const description = `We might contact you by telephone if more information is needed.`;

export const ContactUserOption = () => {
  const { data, get, update } = useBuskingStore();
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const [idProvider, setIdProvider] = useState<string>('');
  const [crmId, setCrmId] = useState<string>('');
  const { isLoggedInUser } = useUserData(crmId);
  const { mutate: submitForm, isPending } = useHandleSubmit({
    confirmationRoute: RoutesList.CONFIRMATION,
    endpoint: '/report/v1/busking-and-street-entertainment',
  });

  const payload = useBuskingPayload();

  useEffect(() => {
    if (!crmId) {
      handleLoginPageEvent(msalInstance, setCrmId, setIdProvider);
    }
  }, [crmId, idProvider]);

  const handleChange = (value: string) => {
    update('contactUserOption', value);
    setErrorMessages([]);
  };

  const contactUserOption = get('contactUserOption');
  const receiveConfirmationEmail = get('receiveConfirmationEmail');

  const isUserDetailsPage =
    (receiveConfirmationEmail.toLowerCase() === 'yes' &&
      contactUserOption.toLowerCase() === 'no') ||
    contactUserOption.toLowerCase() === 'yes';
  const handleNext = () => {
    try {
      contactUserOptionsSchema.parse({
        canWeContactYou: get('contactUserOption'),
      });
      isUserDetailsPage
        ? navigate(RoutesList.YOUR_DETAILS)
        : submitForm({
            payload,
            isLoggedInUser,
          });
    } catch (error) {
      const validationErrors = parseZodErrors(error as ZodError);
      setErrorMessages(
        validationErrors.map((error: ErrorMessages) => ({
          ...error,
          href: error.path,
        })),
      );
    }
  };

  const buttonNext = {
    label:
      isUserDetailsPage || data?.contactUserOption === '' ? 'Next' : 'Submit',
    onClick: handleNext,
    loading: isPending,
  };

  const buttonPrevious = {
    onClick: () => navigate(RoutesList.EMAIL_CONFIRMATION_REPORT),
  };
  const options = [
    {
      labelText: 'Yes',
      isDefault: data.contactUserOption === 'Yes',
    },
    {
      labelText: 'No',
      isDefault: data.contactUserOption === 'No',
    },
  ];

  return (
    <>
      <BinaryQuestionTemplate
        title={pageTitle}
        description={description}
        options={options}
        onChange={handleChange}
        buttonNext={buttonNext}
        buttonPrevious={buttonPrevious}
        errorPath="canWeContactYou"
        errorMessages={errorMessages}
      />
      {data?.contactUserOption?.toLowerCase() === 'no' && (
        <div className="my-4">
          <InformationBox
            title={infoBoxTitle}
            infoText={<InfoText />}
            data-testid="information-box"
            variant="warning"
          />
        </div>
      )}
    </>
  );
};
